"use client";

import { OrbitControls } from "@react-three/drei";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { Suspense, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Mesh } from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

import CanvasLoader from "@/components/modules/loader/Loader";
import { mendakoModelOptions } from "@/constants/scene";
import { lerp } from "three/src/math/MathUtils.js";
import * as THREE from "three";

function MeshComponent({ scrollProgress }: { scrollProgress: number }) {
	const fileUrl = "/space_boi.glb";
	// biome-ignore lint/style/noNonNullAssertion: we are certain that mesh.current is not null or undefined when we access it in effects
	const mesh = useRef<Mesh>(null!);
	const gltf = useLoader(GLTFLoader, fileUrl);
	
	// Rotation targets for smooth interpolation
	const targetRotationY = useRef(0);
	const currentRotationY = useRef(0);
	const targetRotationX = useRef(0);
	const currentRotationX = useRef(0);
	const targetRotationZ = useRef(0);
	const currentRotationZ = useRef(0);
	
	// Position targets for orbit variation
	const targetPosY = useRef(-2.9);
	const currentPosY = useRef(-2.9);
	const targetPosX = useRef(-0.5);
	const currentPosX = useRef(-0.5);
	const targetPosZ = useRef(-0.5);
	const currentPosZ = useRef(-0.5);

	useFrame(() => {
		if (!mesh.current) return;

		// Y-axis rotation: primary horizontal rotation (2 full rotations)
		targetRotationY.current = scrollProgress * Math.PI * 4;
		
		// X-axis rotation: dynamic tilt that varies with scroll (orbital tilt)
		// Uses sine wave for smooth up/down tilting motion
		targetRotationX.current = Math.sin(scrollProgress * Math.PI * 2) * 0.3;
		
		// Z-axis rotation: subtle roll effect (like a barrel roll)
		// Uses cosine for different phase, creating more complex motion
		targetRotationZ.current = Math.cos(scrollProgress * Math.PI * 3) * 0.2;

		// Smooth interpolation for all rotations
		currentRotationY.current = lerp(
			currentRotationY.current,
			targetRotationY.current,
			0.05,
		);
		currentRotationX.current = lerp(
			currentRotationX.current,
			targetRotationX.current,
			0.05,
		);
		currentRotationZ.current = lerp(
			currentRotationZ.current,
			targetRotationZ.current,
			0.05,
		);
		
		mesh.current.rotation.y = currentRotationY.current;
		mesh.current.rotation.x = currentRotationX.current;
		mesh.current.rotation.z = currentRotationZ.current;

		// Y-axis orbit variation: vertical movement (up/down orbit)
		// Creates an orbital path on the Y axis
		targetPosY.current = -2.9 + Math.sin(scrollProgress * Math.PI * 2) * 1.5;
		
		// X-axis orbit: horizontal orbit variation
		targetPosX.current = -0.5 + Math.cos(scrollProgress * Math.PI * 2.5) * 0.8;
		
		// Z-axis orbit: depth variation for 3D orbit effect
		targetPosZ.current = -0.5 + Math.sin(scrollProgress * Math.PI * 1.5) * 0.6;

		// Smooth interpolation for positions
		currentPosY.current = lerp(currentPosY.current, targetPosY.current, 0.05);
		currentPosX.current = lerp(currentPosX.current, targetPosX.current, 0.05);
		currentPosZ.current = lerp(currentPosZ.current, targetPosZ.current, 0.05);
		
		mesh.current.position.y = currentPosY.current;
		mesh.current.position.x = currentPosX.current;
		mesh.current.position.z = currentPosZ.current;
	});

	return (
		<mesh ref={mesh}>
			<primitive object={gltf.scene} />
		</mesh>
	);
}

function Scene({ scrollProgress }: { scrollProgress: number }) {
	return (
		<>
			<OrbitControls
				maxPolarAngle={2}
				minPolarAngle={1.5}
				maxAzimuthAngle={(Math.PI / 2) * 0.5}
				minAzimuthAngle={(-Math.PI / 2) * 0.5}
				enableZoom={false}
				enablePan={false}
			/>
			<ambientLight intensity={0.5} />
			<pointLight position={[10, 10, 10]} intensity={1} />
			<Suspense fallback={<CanvasLoader />}>
				<MeshComponent scrollProgress={scrollProgress} />
			</Suspense>
		</>
	);
}

function ScrollController({
	children,
}: {
	children: (scrollProgress: number) => React.ReactNode;
}) {
	const [scrollProgress, setScrollProgress] = useState(0);

	useEffect(() => {
		const handleScroll = () => {
			const scrollTop = window.scrollY || document.documentElement.scrollTop;
			const documentHeight =
				document.documentElement.scrollHeight - window.innerHeight;
			const progress = documentHeight > 0 ? scrollTop / documentHeight : 0;
			setScrollProgress(Math.min(Math.max(progress, 0), 1));
		};

		// Throttle scroll updates for better performance
		let ticking = false;
		const throttledScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					handleScroll();
					ticking = false;
				});
				ticking = true;
			}
		};

		window.addEventListener("scroll", throttledScroll, { passive: true });
		handleScroll(); // Initial calculation

		return () => {
			window.removeEventListener("scroll", throttledScroll);
		};
	}, []);

	return <>{children(scrollProgress)}</>;
}

export function SpaceBoi() {
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	const canvasContent = (
		<div
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				width: "100vw",
				height: "100vh",
				zIndex: -3,
				pointerEvents: "none",
			}}
		>
			<Canvas
				style={{
					width: "100%",
					height: "100%",
				}}
			>
				<color attach="background" args={[0x000000]} />
				<ScrollController>
					{(scrollProgress) => (
						<Scene scrollProgress={scrollProgress} />
					)}
				</ScrollController>
			</Canvas>
		</div>
	);

	if (!mounted) return null;

	return createPortal(canvasContent, document.body);
}
